import { useState } from 'react'
import {
  Combobox,
  Pill,
  PillsInput,
  useCombobox,
} from '@mantine/core'
import { Mail } from 'lucide-react'

interface CreatableMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  data?: string[];
  label?: string;
  description?: string;
  placeholder?: string;
  error?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  validateItem?: (item: string) => boolean;
  disabled?: boolean;
  required?: boolean;
}

export const CreatableMultiSelect = ({
                                       value,
                                       onChange,
                                       data = [],
                                       label,
                                       description,
                                       placeholder = 'Type and press Enter',
                                       error,
                                       size = 'sm',
                                       validateItem,
                                       disabled = false,
                                       required = false,
                                     }: CreatableMultiSelectProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  })

  const [search, setSearch] = useState('')

  // Filter predefined options that aren't already selected
  const availableOptions = data.filter((option) =>
    !value.includes(option) &&
    option.toLowerCase().includes(search.toLowerCase()),
  )

  const handleValueSelect = (val: string) => {
    const trimmedVal = val.trim()

    if (!trimmedVal) return

    // Validate if validator is provided
    if (validateItem && !validateItem(trimmedVal)) {
      return
    }

    // Don't add duplicates
    if (value.includes(trimmedVal)) {
      setSearch('')
      return
    }

    onChange([...value, trimmedVal])
    setSearch('')
  }

  const handleValueRemove = (val: string) => {
    if (disabled) return
    onChange(value.filter((v) => v !== val))
  }

  // const values = value.map((item) => (
  //   <Pill key={item} withRemoveButton onRemove={() => handleValueRemove(item)}>
  //     {item}
  //   </Pill>
  // ))

  const exactOptionMatch = value.some((item) => item === search.trim())

  return (
    <Combobox store={combobox} onOptionSubmit={handleValueSelect} withinPortal={false}>
      <Combobox.DropdownTarget>
        <PillsInput
          label={label}
          description={description}
          error={error}
          size={size}
          leftSection={<Mail size={16} />}
          onClick={() => !disabled && combobox.openDropdown()}
          disabled={disabled}
          required={required}
        >
          <Pill.Group>
            {value.map((item) => (
              <Pill key={item} withRemoveButton={!disabled} onRemove={() => handleValueRemove(item)}>
                {item}
              </Pill>
            ))}
            <Combobox.EventsTarget>
              <PillsInput.Field
                value={search}
                placeholder={value.length === 0 ? placeholder : undefined}
                onChange={(event) => {
                  if (disabled) return
                  combobox.updateSelectedOptionIndex()
                  setSearch(event.currentTarget.value)
                }}
                onFocus={() => !disabled && combobox.openDropdown()}
                onBlur={() => {
                  combobox.closeDropdown()
                  setSearch('')
                }}
                onKeyDown={(event) => {
                  if (disabled) return
                  if (event.key === 'Backspace' && search.length === 0) {
                    event.preventDefault()
                    if (value.length > 0) {
                      handleValueRemove(value[value.length - 1])
                    }
                  }
                }}
                disabled={disabled}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          {/* Show predefined options */}
          {availableOptions.map((option) => (
            <Combobox.Option key={option} value={option}>
              {option}
            </Combobox.Option>
          ))}

          {/* Show "Add new" option for custom input */}
          {!exactOptionMatch && search.trim().length > 0 && !availableOptions.includes(search.trim()) && (
            <Combobox.Option value={search.trim()}>+ Add {search.trim()}</Combobox.Option>
          )}

          {/* Already added message */}
          {exactOptionMatch && search.trim().length > 0 && (
            <Combobox.Empty>Already added</Combobox.Empty>
          )}

          {/* Empty state messages */}
          {search.trim().length === 0 && availableOptions.length > 0 && (
            <Combobox.Empty>Select from list or type to add</Combobox.Empty>
          )}
          {search.trim().length === 0 && availableOptions.length === 0 && data.length === 0 && (
            <Combobox.Empty>Type to add email</Combobox.Empty>
          )}
          {search.trim().length === 0 && availableOptions.length === 0 && data.length > 0 && (
            <Combobox.Empty>All options selected</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
